import React from "react";
import { Switch } from "@mui/material";
import { FaTrashAlt } from "react-icons/fa";

const VariantEditorSection = ({
  variants,
  errors,
  onAddVariant,
  onRemoveVariant,
  onVariantFieldChange,
  onVariantImageChange,
  onVariantGalleryChange,
  onDeleteVariantGalleryImage,
  onClearVariantImage,
  onSetDefaultVariant,
}) => (
  <div className="variant-section">
    <div className="variant-section-header">
      <div>
        <h3 className="section-title variant-section-title">Product Variants</h3>
        <p className="variant-section-copy">
          Add configurations like storage, color, grade, or RAM for the same item.
        </p>
      </div>
      <button type="button" className="variant-add-button" onClick={onAddVariant}>
        + Add Variant
      </button>
    </div>

    {variants.length === 0 ? (
      <div className="variant-empty-state">
        No variants added yet. Leave this empty for a single product listing.
      </div>
    ) : null}

    {variants.map((variant, index) => {
      const variantErrors = errors[variant.client_id] || {};

      return (
        <div key={variant.client_id} className="variant-card">
          <div className="variant-card-header">
            <div>
              <h4 className="variant-card-title">Variant {index + 1}</h4>
              <p className="variant-card-subtitle">{variant.variant_name || "New variant"}</p>
            </div>
            <div className="variant-card-actions">
              <label className="variant-default-radio">
                <input
                  type="radio"
                  name="defaultVariant"
                  checked={Number(variant.is_default) === 1}
                  onChange={() => onSetDefaultVariant(variant.client_id)}
                />
                <span>Default</span>
              </label>
              <button
                type="button"
                className="variant-remove-button"
                onClick={() => onRemoveVariant(variant.client_id)}
              >
                <FaTrashAlt />
              </button>
            </div>
          </div>

          <div className="variant-grid">
            <div>
              <label className="label">Variant Name</label>
              <input
                type="text"
                className="form-control input"
                value={variant.variant_name}
                onChange={(e) => onVariantFieldChange(variant.client_id, "variant_name", e.target.value)}
                placeholder="Example: 16GB RAM / 512GB SSD"
              />
              {variantErrors.variant_name ? (
                <div className="error-message">{variantErrors.variant_name}</div>
              ) : null}
            </div>

            <div>
              <label className="label">Title</label>
              <input
                type="text"
                className="form-control input"
                value={variant.title}
                onChange={(e) => onVariantFieldChange(variant.client_id, "title", e.target.value)}
                placeholder="Optional display title"
              />
            </div>

            <div>
              <label className="label">SKU</label>
              <input
                type="text"
                className="form-control input"
                value={variant.sku}
                onChange={(e) => onVariantFieldChange(variant.client_id, "sku", e.target.value)}
                placeholder="Variant SKU"
              />
            </div>

            <div>
              <label className="label">Actual Price</label>
              <input
                type="number"
                className="form-control input"
                value={variant.actual_price}
                onChange={(e) => onVariantFieldChange(variant.client_id, "actual_price", e.target.value)}
                placeholder="Enter actual price"
              />
              {variantErrors.actual_price ? (
                <div className="error-message">{variantErrors.actual_price}</div>
              ) : null}
            </div>

            <div>
              <label className="label">Selling Price</label>
              <input
                type="number"
                className="form-control input"
                value={variant.selling_price}
                onChange={(e) => onVariantFieldChange(variant.client_id, "selling_price", e.target.value)}
                placeholder="Enter selling price"
              />
              {variantErrors.selling_price ? (
                <div className="error-message">{variantErrors.selling_price}</div>
              ) : null}
            </div>

            <div>
              <label className="label">Offer Price</label>
              <input
                type="number"
                className="form-control input"
                value={variant.offer_price}
                onChange={(e) => onVariantFieldChange(variant.client_id, "offer_price", e.target.value)}
                placeholder="Optional offer price"
              />
              {variantErrors.offer_price ? (
                <div className="error-message">{variantErrors.offer_price}</div>
              ) : null}
            </div>

            <div>
              <label className="label">Tax (%)</label>
              <input
                type="number"
                className="form-control input"
                value={variant.tax}
                onChange={(e) => onVariantFieldChange(variant.client_id, "tax", e.target.value)}
                placeholder="Tax percentage"
              />
              {variantErrors.tax ? <div className="error-message">{variantErrors.tax}</div> : null}
            </div>

            <div>
              <label className="label">Discount (%)</label>
              <input
                type="number"
                className="form-control input"
                value={variant.discount}
                onChange={(e) => onVariantFieldChange(variant.client_id, "discount", e.target.value)}
                placeholder="Discount"
              />
              {variantErrors.discount ? (
                <div className="error-message">{variantErrors.discount}</div>
              ) : null}
            </div>

            <div>
              <label className="label">Stock</label>
              <input
                type="number"
                className="form-control input"
                value={variant.stock}
                onChange={(e) => onVariantFieldChange(variant.client_id, "stock", e.target.value)}
                placeholder="Available stock"
              />
              {variantErrors.stock ? <div className="error-message">{variantErrors.stock}</div> : null}
            </div>

            <div>
              <label className="label">Maximum Order Quantity</label>
              <input
                type="number"
                className="form-control input"
                value={variant.maximum_cart_quantity}
                onChange={(e) =>
                  onVariantFieldChange(variant.client_id, "maximum_cart_quantity", e.target.value)
                }
                placeholder="Maximum quantity per order"
              />
              {variantErrors.maximum_cart_quantity ? (
                <div className="error-message">{variantErrors.maximum_cart_quantity}</div>
              ) : null}
            </div>

            <div>
              <label className="label">Net Weight</label>
              <input
                type="text"
                className="form-control input"
                value={variant.net_weight}
                onChange={(e) => onVariantFieldChange(variant.client_id, "net_weight", e.target.value)}
                placeholder="Optional net weight"
              />
            </div>

            <div>
              <label className="label">Dimensions</label>
              <input
                type="text"
                className="form-control input"
                value={variant.dimensions}
                onChange={(e) => onVariantFieldChange(variant.client_id, "dimensions", e.target.value)}
                placeholder="Optional dimensions"
              />
            </div>

            <div>
              <label className="label">Sort Order</label>
              <input
                type="number"
                className="form-control input"
                value={variant.sort_order}
                onChange={(e) => onVariantFieldChange(variant.client_id, "sort_order", e.target.value)}
                placeholder="Display order"
              />
            </div>

            <div className="variant-image-field">
              <label className="label">Variant Image</label>
              <label className="variant-image-picker">
                <span>{variant.imagePreview ? "Change image" : "Upload image"}</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => onVariantImageChange(variant.client_id, e.target.files?.[0] || null)}
                />
              </label>

              {variant.imagePreview ? (
                <div className="variant-image-preview-block">
                  <img src={variant.imagePreview} alt={variant.variant_name || `Variant ${index + 1}`} />
                  <button
                    type="button"
                    className="variant-clear-image-button"
                    onClick={() => onClearVariantImage(variant.client_id)}
                  >
                    Remove image
                  </button>
                </div>
              ) : null}
            </div>

            <div className="variant-image-field">
              <label className="label">Gallery Images</label>
              <label className="variant-image-picker">
                <span>+ Add Gallery Images</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => onVariantGalleryChange(variant.client_id, Array.from(e.target.files || []))}
                />
              </label>

              {[...variant.galleryImages, ...variant.galleryPreviews].length > 0 ? (
                <div className="variant-gallery-preview-grid">
                  {[...variant.galleryImages, ...variant.galleryPreviews].map((image, galleryIndex) => (
                    <div key={`${image}-${galleryIndex}`} className="variant-gallery-item">
                      <img src={image} alt={`${variant.variant_name || "Variant"} ${galleryIndex + 1}`} />
                      <button
                        type="button"
                        className="delete-button"
                        onClick={() => onDeleteVariantGalleryImage(variant.client_id, galleryIndex)}
                      >
                        <FaTrashAlt className="trash-icon" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div className="variant-description-field">
            <label className="label">Description</label>
            <textarea
              className="form-control textarea"
              value={variant.description}
              onChange={(e) => onVariantFieldChange(variant.client_id, "description", e.target.value)}
              placeholder="Optional variant-specific description"
            />
          </div>

          <div className="variant-switch-row">
            <div className="switch-container">
              <span className="switch-label">Status</span>
              <Switch
                checked={Number(variant.status) === 1}
                onChange={(e) =>
                  onVariantFieldChange(variant.client_id, "status", e.target.checked ? 1 : 0)
                }
              />
            </div>

            <div className="switch-container">
              <span className="switch-label">Refurbished</span>
              <Switch
                checked={Number(variant.refurbished) === 1}
                onChange={(e) =>
                  onVariantFieldChange(variant.client_id, "refurbished", e.target.checked ? 1 : 0)
                }
              />
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

export default VariantEditorSection;
